import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import AnimateHeight from 'react-animate-height';
import CardContainer from 'src/components/CardContainer/CardContainer';
import { ExpandIcon } from 'src/assets/icons/expandIcon';
import { UsersList } from 'src/components/UsersList/UsersList';
import { PaginationComponent } from 'src/components/Pagination/PaginationComponent';
import { AddUserPopup } from 'src/components/AddUserPopup/AddUserPopup';
import { usePagination } from 'src/hooks/usePagination';
import { useGetUsersAllQuery } from 'src/services/usersService';
import { useAppSelector } from 'src/store';
import { ROLES } from 'src/constants/roles';
import type { User } from 'src/services/usersService';

const Counter = ({ items }: { items?: unknown[] }) =>
  items ? <div className="yellow-counter">{items.length}</div> : null;

interface TeamCardProps {
  userInfo: Pick<User, 'id' | 'role'> | undefined;
  disabled?: boolean;
}

// Self-contained team/users section: fetches its own data, owns pagination
// and the expand/collapse state, and reads plan status directly from the store.
// Never enters edit mode itself, but participates in the account cards'
// mutual exclusion by graying out while another card is being edited.
export const TeamCard = ({ userInfo, disabled = false }: TeamCardProps) => {
  const { data: users, isFetching: isUsersFetching } = useGetUsersAllQuery();
  const { isInactivePlan } = useAppSelector((s) => s.planSlice);
  const [usersHeight, setUsersHeight] = useState<number | 'auto'>('auto');
  const [paginatedUsers, setPaginatedUsers] = useState([]);
  const { onChangeRowsPerPage, onChangePage, pagination } = usePagination();

  useEffect(() => {
    if (users) {
      const pagUsers = [...users]
        .sort((a, b) => {
          const first = a.first_name.toLowerCase();
          const second = b.first_name.toLowerCase();

          if (first < second) {
            return -1;
          }
          if (first > second) {
            return 1;
          }
          return 0;
        })
        .reduce((resultArray, item, index) => {
          const chunkIndex = Math.floor(index / pagination.rowsPerPage);

          if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = [];
          }

          resultArray[chunkIndex].push(item);

          return resultArray;
        }, []);

      setPaginatedUsers(pagUsers);
    }
  }, [users, pagination]);

  useEffect(() => {
    if (isUsersFetching) {
      onChangePage(1);
    }
  }, [isUsersFetching]);

  const isManagerRole = userInfo?.role === ROLES.MANAGER;

  return (
    <CardContainer className={clsx('team', disabled && 'disabled')}>
      <div className="card-header z-10">
        <button
          type="button"
          className={clsx('cursor-pointer title', !usersHeight && 'collapsed', 'text-left')}
          onClick={() => setUsersHeight(usersHeight ? 0 : 'auto')}
        >
          <Counter items={users} />
          <span>Users</span>
          <ExpandIcon />
        </button>
        <div>
          <AddUserPopup disabled={isInactivePlan} />
        </div>
      </div>
      <AnimateHeight height={usersHeight}>
        <div className="card-body">
          <UsersList
            isPlanInactive={isInactivePlan}
            users={paginatedUsers[pagination.page - 1]}
            userMe={userInfo}
            isManagerRole={isManagerRole}
          />
          <div className="w-full flex justify-end">
            {Boolean(users?.length) && (
              <PaginationComponent
                rowsPerPageOptions={[5, 10, 15]}
                count={users.length}
                rowsPerPage={pagination.rowsPerPage}
                currentPage={pagination.page}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
              />
            )}
          </div>
        </div>
      </AnimateHeight>
    </CardContainer>
  );
};
